import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WaitlistForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    willPay: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [waitlistCount, setWaitlistCount] = useState(1247);

  useEffect(() => {
    fetchWaitlistCount();
  }, []);

  const fetchWaitlistCount = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/waitlist/count`);
      setWaitlistCount(res.data.count);
    } catch (err) {
      console.error('Failed to fetch waitlist count', err);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setError('');
  };

  const toggleWillPay = () => {
    setFormData((prev) => ({
      ...prev,
      willPay: !prev.willPay,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.role) {
      setError('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const res = await axios.post(`${BACKEND_URL}/api/waitlist`, formData);

      if (res.data.success) {
        setIsSuccess(true);
        setFormData({
          name: '',
          email: '',
          role: '',
          willPay: false,
        });
        fetchWaitlistCount();
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setError('This email is already on the waitlist.');
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="waitlist" className="py-24 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="text-center space-y-6 mb-12">
          <h2 className="text-4xl md:text-5xl font-bold">
            Get{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Early Access
            </span>
          </h2>

          <p className="text-xl text-gray-400">
            Access will be rolled out in limited batches.
            <br />
            Join{' '}
            <span className="text-cyan-400 font-semibold">
              {waitlistCount.toLocaleString()}
            </span>{' '}
            engineer{waitlistCount !== 1 ? 's' : ''} already onboard.
          </p>
        </div>

        {!isSuccess ? (
          <form
            onSubmit={handleSubmit}
            className="space-y-6 p-8 bg-gradient-to-br from-gray-900/50 to-gray-800/30 border border-gray-800 rounded-2xl"
          >
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-gray-300">Full Name</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                className="h-12 bg-gray-900/50 border-gray-700 text-white"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label className="text-gray-300">Email Address</Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@company.com"
                className="h-12 bg-gray-900/50 border-gray-700 text-white"
              />
            </div>

            {/* Role */}
            <div className="space-y-2">
              <Label className="text-gray-300">Role / Title</Label>
              <Input
                name="role"
                value={formData.role}
                onChange={handleChange}
                placeholder="Senior Software Engineer"
                className="h-12 bg-gray-900/50 border-gray-700 text-white"
              />
            </div>

            {/* Will Pay Toggle */}
            <div className="space-y-3">
              <Label className="text-gray-300">
                Would you pay for this product?
              </Label>

              <div
                onClick={toggleWillPay}
                className={`flex items-center justify-between h-14 px-4 rounded-lg border cursor-pointer transition-all
                  ${
                    formData.willPay
                      ? 'bg-cyan-500/10 border-cyan-500/40'
                      : 'bg-gray-900/50 border-gray-700'
                  }`}
              >
                <span className="text-white">
                  {formData.willPay
                    ? 'Yes, I’d pay for this 💳'
                    : 'No, just exploring 👀'}
                </span>

                <div
                  className={`w-12 h-6 rounded-full p-1 flex items-center transition-all
                    ${
                      formData.willPay ? 'bg-cyan-500' : 'bg-gray-600'
                    }`}
                >
                  <div
                    className={`w-4 h-4 bg-white rounded-full transition-transform
                      ${
                        formData.willPay
                          ? 'translate-x-6'
                          : 'translate-x-0'
                      }`}
                  />
                </div>
              </div>

            
            </div>

            {/* Error */}
            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-14 bg-gradient-to-r from-cyan-500 to-blue-600 text-lg font-semibold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Joining...
                </>
              ) : (
                <>
                  Join the First Batch
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>

            <p className="text-center text-gray-500 text-sm">
              No spam. Early users get priority.
            </p>
          </form>
        ) : (
          <div className="p-12 rounded-2xl text-center bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border border-cyan-500/20">
            <CheckCircle className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">
              You’re on the list!
            </h3>
            <p className="text-gray-400">
              We’ll notify you when early access opens.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default WaitlistForm;
